import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    TemplateRef,
} from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { TreeViewFlatNode } from '@app/shared/models/tree-view.model';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { SelectionModel } from '@angular/cdk/collections';
import { MultipleSelectionListItem } from '@app/shared/models/multiple-selection-list.model';

@Component({
    selector: 'app-multiple-selection-tree',
    templateUrl: './multiple-selection-tree.component.html',
    styleUrls: ['./multiple-selection-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MultipleSelectionTreeComponent<T> {
    @Input() set data(source: MultipleSelectionListItem<T>[]) {
        this.dataSource.data = source || [];

        this.listSelection = new SelectionModel<MultipleSelectionListItem<T>>(true);
        this.treeControl.dataNodes.forEach((node) => {
            if (node.item.selected) {
                this.listSelection.select(node.item);
            }
        });

        this.treeControl.expandAll();
    }
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    @Input() nodeTemplateRef: TemplateRef<any>;
    @Input() disableBranchSelection = false;
    @Output() selectionChange: EventEmitter<T[]> = new EventEmitter<T[]>();

    treeControl = new FlatTreeControl<TreeViewFlatNode<MultipleSelectionListItem<T>>>(
        (node) => node.level,
        (node) => node.expandable
    );

    /* eslint-disable @typescript-eslint/unbound-method, @typescript-eslint/no-unsafe-return */
    treeFlattener = new MatTreeFlattener(
        this.treeNodeTransformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children
    );
    /* eslint-enable @typescript-eslint/unbound-method, @typescript-eslint/no-unsafe-return */

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    listSelection = new SelectionModel<MultipleSelectionListItem<T>>(true);

    hasChild = (_: number, node: TreeViewFlatNode<MultipleSelectionListItem<T>>) => node.expandable;

    /** Whether all the descendants of the node are selected. */
    descendantsAllSelected(node: TreeViewFlatNode<MultipleSelectionListItem<T>>): boolean {
        const descendants = this.treeControl.getDescendants(node);
        const descAllSelected =
            descendants.length > 0 &&
            descendants.every((child) => {
                return this.listSelection.isSelected(child.item);
            });
        return descAllSelected;
    }

    /** Whether part of the descendants are selected */
    descendantsPartiallySelected(node: TreeViewFlatNode<MultipleSelectionListItem<T>>): boolean {
        const descendants = this.treeControl.getDescendants(node);
        const result = descendants.some((child) => this.listSelection.isSelected(child.item));
        return result && !this.descendantsAllSelected(node);
    }

    /** Toggle the to-do item selection. Select/deselect all the descendants node */
    itemSelectionToggle(node: TreeViewFlatNode<MultipleSelectionListItem<T>>): void {
        this.listSelection.toggle(node.item);
        const descendants = this.treeControl.getDescendants(node).map((current) => current.item);
        this.listSelection.isSelected(node.item)
            ? this.listSelection.select(...descendants)
            : this.listSelection.deselect(...descendants);

        // Force update for the parent
        descendants.forEach((child) => this.listSelection.isSelected(child));
        this.checkAllParentsSelection(node);

        this.emitSelectionChange();
    }

    /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
    leafItemSelectionToggle(node: TreeViewFlatNode<MultipleSelectionListItem<T>>): void {
        this.listSelection.toggle(node.item);
        this.checkAllParentsSelection(node);

        this.emitSelectionChange();
    }

    /** Checks all the parents when a leaf node is selected/unselected */
    checkAllParentsSelection(node: TreeViewFlatNode<MultipleSelectionListItem<T>>): void {
        let parent: TreeViewFlatNode<MultipleSelectionListItem<T>> | null =
            this.getParentNode(node);
        while (parent) {
            this.checkRootNodeSelection(parent);
            parent = this.getParentNode(parent);
        }
    }

    /** Check root node checked state and change it accordingly */
    checkRootNodeSelection(node: TreeViewFlatNode<MultipleSelectionListItem<T>>): void {
        const nodeSelected = this.listSelection.isSelected(node.item);
        const descendants = this.treeControl.getDescendants(node);
        const descAllSelected =
            descendants.length > 0 &&
            descendants.every((child) => {
                return this.listSelection.isSelected(child.item);
            });
        if (nodeSelected && !descAllSelected) {
            this.listSelection.deselect(node.item);
        } else if (!nodeSelected && descAllSelected) {
            this.listSelection.select(node.item);
        }
    }

    /** Get the parent node of a node */
    getParentNode(
        node: TreeViewFlatNode<MultipleSelectionListItem<T>>
    ): TreeViewFlatNode<MultipleSelectionListItem<T>> | null {
        const currentLevel = this.treeFlattener.getLevel(node);

        if (currentLevel < 1) {
            return null;
        }

        const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

        for (let i = startIndex; i >= 0; i--) {
            const currentNode = this.treeControl.dataNodes[i];

            if (this.treeFlattener.getLevel(currentNode) < currentLevel) {
                return currentNode;
            }
        }
        return null;
    }

    emitSelectionChange(): void {
        const selected: MultipleSelectionListItem<T>[] = [];

        this.treeControl.dataNodes.forEach((node) => {
            node.item.selected = this.listSelection.isSelected(node.item);

            if (node.item.selected) {
                selected.push(node.item);
            }
        });

        this.selectionChange.emit(selected.map((item) => item.element));
    }

    private treeNodeTransformer(
        node: MultipleSelectionListItem<T>,
        level: number
    ): TreeViewFlatNode<MultipleSelectionListItem<T>> {
        /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
        const collection: unknown[] = node.children;

        return {
            expandable: !!collection && collection.length > 0,
            level: level,
            item: node,
        };
    }
}
